import { Fragment, ReactNode, useId } from "react";
import { isNull, isUndefined, partitionByPredicates } from "@silo/utils";
import { match } from "ts-pattern";
import { ArcherContainer, ArcherElement } from "react-archer";
import { assetKeysLegacy, ChecksumAddress } from "@silo/types";
import { classNames } from "../../utils/class-names";
import { EmptyText } from "../empty-text";
import { AssetAddressContext } from "../asset-address-context";

export interface RouterGraphProps {
  firstSilo: RouterGraphSilo;
  secondSilo?: RouterGraphSilo;
  assetLogo: ReactNode;
  startingLabel?: boolean;
}

interface RouterGraphAsset {
  assetAddress: ChecksumAddress;
  symbol: string;
  action?: "deposit" | "borrow";
}

interface RouterGraphSilo {
  baseAsset: RouterGraphAsset;
  bridgeAssets: RouterGraphAsset[];
}

export const RouterGraph = ({
  firstSilo,
  secondSilo,
  assetLogo,
  startingLabel = true,
}: RouterGraphProps) => {
  const { _tag, silos } = prepareGraphData(firstSilo, secondSilo);
  const id = useId();

  return (
    <div className="flex flex-col gap-5 @container">
      <ArcherContainer
        strokeColor="white"
        endShape={{ arrow: { arrowLength: 4, arrowThickness: 5 } }}
        offset={4}
      >
        <div className="flex @lg:flex-col gap-2.5">
          {silos.map(({ symbol, assets }, siloIndex) => (
            <div
              className="flex-1 max-w-xl p-5 rounded-lg bg-shale-750"
              key={symbol}
            >
              <div className="flex flex-col justify-between h-full gap-5">
                <div className="text-sm font-medium text-white">
                  {symbol} isolated silo
                </div>
                <div className="flex flex-col gap-8 @lg:flex-row @lg:justify-between">
                  {assets.map((asset) => {
                    if (isNull(asset)) {
                      // return spacer
                      return (
                        <div
                          className={classNames(
                            "px-3",
                            _tag === "dual"
                              ? "py-1.5"
                              : "py-4 @lg:py-1.5 hidden @lg:block"
                          )}
                        >
                          <div
                            className={
                              _tag === "dual" ? "block" : "hidden @lg:block"
                            }
                          >
                            <EmptyText className="text-xs" />
                            <EmptyText className="text-sm" />
                          </div>
                          <div
                            className={classNames("flex @lg:min-w-[110px]")}
                          ></div>
                        </div>
                      );
                    }

                    const { action, symbol, assetAddress } = asset;

                    const jsx = (
                      <div
                        className={classNames(
                          "flex items-center px-3 rounded-lg bg-shale-700",
                          action ? "" : "opacity-30",
                          _tag === "dual" ? "py-1.5" : "py-4 @lg:py-1.5"
                        )}
                      >
                        <div
                          className={
                            _tag === "dual" ? "block" : "hidden @lg:block"
                          }
                        >
                          <EmptyText className="text-xs" />
                          <EmptyText className="text-sm" />
                        </div>
                        <div
                          className={classNames(
                            "flex @lg:min-w-[110px]",
                            _tag === "dual"
                              ? "flex-col gap-0 items-start justify-center h-full"
                              : "@lg:flex-col gap-5 @lg:gap-0 items-center @lg:items-start @lg:justify-center @lg:h-full"
                          )}
                        >
                          {match(action)
                            .with("deposit", () => (
                              <div className="text-xs font-medium text-shale-400">
                                Deposit
                              </div>
                            ))
                            .with("borrow", () => (
                              <div className="text-xs font-medium text-shale-400">
                                Borrow
                              </div>
                            ))
                            .otherwise(() => (
                              <></>
                            ))}

                          <div className="flex items-center gap-2.5">
                            <AssetAddressContext.Provider value={assetAddress}>
                              {assetLogo}
                            </AssetAddressContext.Provider>
                            <div className="text-sm font-medium text-white">
                              {symbol}
                            </div>
                          </div>
                        </div>
                      </div>
                    );

                    return (
                      <Fragment key={asset.assetAddress}>
                        <div className="hidden @lg:block">
                          <ArcherElement
                            id={[id, siloIndex, action, "horizontal"].join("-")}
                            relations={[
                              ...(action === "deposit"
                                ? [
                                    {
                                      targetId: [
                                        id,
                                        siloIndex,
                                        "borrow",
                                        "horizontal",
                                      ].join("-"),
                                      targetAnchor: "left",
                                      sourceAnchor: "right",
                                      style: { strokeDasharray: "5,7" },
                                    } as const,
                                  ]
                                : []),
                              ...(_tag === "dual" && action === "borrow"
                                ? [
                                    {
                                      targetId: [
                                        id,
                                        siloIndex + 1,
                                        "deposit",
                                        "horizontal",
                                      ].join("-"),
                                      targetAnchor: "top",
                                      sourceAnchor: "bottom",
                                      style: { strokeDasharray: "5,7" },
                                    } as const,
                                  ]
                                : []),
                            ]}
                          >
                            {jsx}
                          </ArcherElement>
                        </div>
                        <div className="block @lg:hidden">
                          <ArcherElement
                            id={[id, siloIndex, action, "vertical"].join("-")}
                            relations={[
                              ...(action === "deposit"
                                ? [
                                    {
                                      targetId: [
                                        id,
                                        siloIndex,
                                        "borrow",
                                        "vertical",
                                      ].join("-"),
                                      targetAnchor: "top",
                                      sourceAnchor: "bottom",
                                      style: { strokeDasharray: "5,7" },
                                    } as const,
                                  ]
                                : []),
                              ...(_tag === "dual" && action === "borrow"
                                ? [
                                    {
                                      targetId: [
                                        id,
                                        siloIndex + 1,
                                        "deposit",
                                        "vertical",
                                      ].join("-"),
                                      targetAnchor: "left",
                                      sourceAnchor: "right",
                                      style: { strokeDasharray: "5,7" },
                                    } as const,
                                  ]
                                : []),
                            ]}
                          >
                            {jsx}
                          </ArcherElement>
                        </div>
                      </Fragment>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      </ArcherContainer>
      {startingLabel ? (
        <p className="text-shale-200">
          You are starting {silos.length * 2} positions
        </p>
      ) : null}
    </div>
  );
};

const getSingleSiloAssets = (
  silo: RouterGraphSilo
): (RouterGraphAsset | null)[] => {
  const assets = [silo.baseAsset, ...silo.bridgeAssets];
  const mappedAssets = assets.map((asset, i) => ({
    ...asset,
    key: assetKeysLegacy[i],
  }));

  const [[depositAsset], [borrowAsset], remainingAssets] =
    partitionByPredicates(mappedAssets, isDepositAsset, isBorrowAsset);

  const otherAssets = remainingAssets.length ? remainingAssets : [null];

  if (isUndefined(depositAsset) || isUndefined(borrowAsset)) {
    throw new Error("Invalid arguments: deposit and borrow must be provided");
  }

  if (depositAsset.key === "base") {
    // base to bridge
    return [depositAsset, borrowAsset, ...otherAssets];
  }

  if (borrowAsset.key === "base") {
    // bridge to base
    return [...otherAssets, depositAsset, borrowAsset];
  }

  // bridge to bridge
  return [depositAsset, borrowAsset, ...otherAssets];
};

type PreparedGraphData = {
  _tag: "single" | "dual";
  silos: { symbol: string; assets: (RouterGraphAsset | null)[] }[];
};

const prepareGraphData = (
  firstSilo: RouterGraphSilo,
  secondSilo?: RouterGraphSilo
): PreparedGraphData => {
  if (isUndefined(secondSilo)) {
    const { symbol } = firstSilo.baseAsset;

    return {
      _tag: "single",
      silos: [{ symbol, assets: getSingleSiloAssets(firstSilo) }],
    };
  }

  const mapFirstSilo = () => {
    const { baseAsset, bridgeAssets } = firstSilo;
    const { symbol } = baseAsset;
    const [depositAssets, borrowAssets, otherAssets] = partitionByPredicates(
      [baseAsset, ...bridgeAssets],
      isDepositAsset,
      isBorrowAsset
    );

    return {
      symbol,
      assets: [
        ...depositAssets,
        ...borrowAssets,
        ...(otherAssets.length ? otherAssets : [null]),
      ],
    };
  };

  const mapSecondSilo = () => {
    const { baseAsset, bridgeAssets } = secondSilo;
    const { symbol } = baseAsset;
    const [depositAssets, borrowAssets, otherAssets] = partitionByPredicates(
      [baseAsset, ...bridgeAssets],
      isDepositAsset,
      isBorrowAsset
    );

    return {
      symbol,
      assets: [
        ...(otherAssets.length ? otherAssets : [null]),
        ...depositAssets,
        ...borrowAssets,
      ],
    };
  };

  return { _tag: "dual", silos: [mapFirstSilo(), mapSecondSilo()] };
};

const isDepositAsset = ({ action }: RouterGraphAsset) => action === "deposit";
const isBorrowAsset = ({ action }: RouterGraphAsset) => action === "borrow";

export const PulseRouterGraph = ({
  _tag,
  silos,
}: {
  _tag: "dual" | "single";
  silos: { assetsCount: number }[];
}) => {
  return (
    <div className="flex flex-col gap-5 @container animate-pulse">
      <div className="flex @lg:flex-col gap-2.5">
        {silos.map(({ assetsCount }, index) => {
          return (
            <div
              className="flex-1 max-w-xl p-5 rounded-lg bg-shale-750"
              key={index}
            >
              <div className="flex flex-col justify-between h-full gap-5">
                <div className="h-5 w-28"></div>
                <div className="flex flex-col gap-8 @lg:flex-row @lg:justify-between">
                  {[...Array(assetsCount)].map((_, assetIndex) => {
                    const jsx = (
                      <div
                        className={classNames(
                          "flex items-center px-3 rounded-lg bg-shale-700 opacity-30 @lg:w-32 @lg:h-auto w-full h-[52px]",
                          _tag === "dual" ? "py-1.5" : "py-4 @lg:py-1.5"
                        )}
                      >
                        <div
                          className={
                            _tag === "dual" ? "block" : "hidden @lg:block"
                          }
                        >
                          <EmptyText className="text-xs" />
                          <EmptyText className="text-sm" />
                        </div>
                      </div>
                    );

                    return (
                      <Fragment key={assetIndex}>
                        <div className="hidden @lg:block">{jsx}</div>
                        <div className="block @lg:hidden">{jsx}</div>
                      </Fragment>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="h-6 rounded-lg w-52 bg-shale-700"></div>
    </div>
  );
};
